import React from 'react';
import { AlertTriangleIcon, MapPinIcon, Building2Icon, UsersIcon, TargetIcon, DatabaseIcon, SmartphoneIcon, MessageSquareIcon, MailIcon, PhoneIcon, MonitorIcon, Share2Icon, ArrowUpIcon, ArrowRightIcon, UserRoundIcon } from 'lucide-react';
import { alertCard, inputs, channels, targeted, resultsStrip, callout, WATER_MAIN_PHOTO } from '../../../data/communicationsHero';
type P = {
  isFR: boolean;
};
const pk = (isFR: boolean) => (v: {
  fr: string;
  en: string;
}) => isFR ? v.fr : v.en;
export function AlertCard({
  isFR
}: P) {
  const pick = pk(isFR);
  return <div className="rounded-2xl border border-[#F6E3E3] bg-[#FFF8F7] px-5 py-4 shadow-[0_14px_34px_-26px_rgba(120,36,40,0.35)]" data-id="element-2703" data-mp="6ryntzksthuz">
      <div className="flex items-start gap-4" data-id="element-2704" data-mp="1zx2zp9keitc">
        <span className="w-11 h-11 rounded-full bg-white border border-[#F6E3E3] flex items-center justify-center shrink-0" aria-hidden="true" data-id="element-2705" data-mp="3agih1kh2023b">
          <AlertTriangleIcon className="w-5 h-5 text-[#DC3B3B]" strokeWidth={1.8} data-id="element-2706" data-mp="36dvmvcwwu52y" />
        </span>
        <div className="flex-1 min-w-0" data-id="element-2707" data-mp="2w9kzo82xxv5j">
          <p className="font-sans text-[10.5px] font-semibold tracking-[0.2em] uppercase text-[#DC3B3B]" data-id="element-2708" data-mp="2w8jirmz8uefw">{pick(alertCard.label)}</p>
          <p className="font-serif text-[1.35rem] text-[#121212] leading-tight mt-1" data-id="element-2709" data-mp="35zw21q6z7wab">{pick(alertCard.title)}</p>
          <p className="font-sans text-[12px] text-[#6B7180] mt-1" data-id="element-2710" data-mp="3azs7zuz8hxsi">{pick(alertCard.time)}</p>
        </div>
        <img src={WATER_MAIN_PHOTO} alt="" className="hidden sm:block w-[88px] h-[76px] rounded-lg object-cover shrink-0" data-id="element-2711" data-mp="2dctiq5avfs4n" />
      </div>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-6" data-id="element-2712" data-mp="2ib3dwfwlisq9">
        <p className="flex items-start gap-3 font-sans text-[12px] text-[#3A4050] leading-snug" data-id="element-2713" data-mp="2n94xr31uiw2s">
          <MapPinIcon className="w-[18px] h-[18px] text-[#4F46E5] shrink-0" strokeWidth={1.6} aria-hidden="true" data-id="element-2714" data-mp="2s9sk3bf4qoqo" />
          <span data-id="element-2715" data-mp="2jfiwq157aa3">
            {pick(alertCard.target)}
            <br data-id="element-2716" data-mp="7ia9ie5cq34r" />
            {pick(alertCard.targetSub)}
          </span>
        </p>
        <p className="flex items-start gap-3 font-sans text-[12px] text-[#3A4050] leading-snug" data-id="element-2717" data-mp="23logenfvr868">
          <Building2Icon className="w-[18px] h-[18px] text-[#4F46E5] shrink-0" strokeWidth={1.6} aria-hidden="true" data-id="element-2718" data-mp="1yn3rvl9zqcxm" />
          <span data-id="element-2719" data-mp="2cmwf36mb6b0u">{pick(alertCard.impact)}</span>
        </p>
      </div>
    </div>;
}
const INPUT_ICONS = {
  identity: UsersIcon,
  context: MapPinIcon,
  audience: TargetIcon,
  data: DatabaseIcon
} as const;
export function InputCard({
  isFR,
  item
}: P & {
  item: (typeof inputs)[number];
}) {
  const pick = pk(isFR);
  const Icon = INPUT_ICONS[item.key];
  return <div className="rounded-xl border border-[#EEEEF4] bg-white px-3.5 py-3 flex items-start gap-3 shadow-[0_8px_20px_-18px_rgba(40,36,120,0.45)]" data-id="element-2720" data-mp="1fu9dotk6youg">
      <span className="w-9 h-9 rounded-full bg-[#F1F0FD] flex items-center justify-center shrink-0" aria-hidden="true" data-id="element-2721" data-mp="1owsez98bwtel">
        <Icon className="w-[17px] h-[17px] text-[#4F46E5]" strokeWidth={1.7} data-id="element-2722" data-mp="115wocffm048x" />
      </span>
      <span className="min-w-0" data-id="element-2723" data-mp="x52znjucelez">
        <span className="block font-sans text-[13px] font-semibold text-[#121212]" data-id="element-2724" data-mp="x3rwnm8q1hox">{pick(item.title)}</span>
        <span className="block font-sans text-[10.5px] text-[#5B6478] leading-snug mt-0.5" data-id="element-2725" data-mp="n031v9r7qfx5">{pick(item.sub)}</span>
      </span>
    </div>;
}
const CHANNEL_ICONS = {
  push: SmartphoneIcon,
  sms: MessageSquareIcon,
  email: MailIcon,
  voice: PhoneIcon,
  web: MonitorIcon,
  social: Share2Icon
} as const;
export function ChannelCard({
  isFR,
  item
}: P & {
  item: (typeof channels)[number];
}) {
  const pick = pk(isFR);
  const Icon = CHANNEL_ICONS[item.key];
  return <div className="rounded-xl border border-[#EEEEF4] bg-white px-3.5 py-2.5 flex items-center gap-3 shadow-[0_8px_20px_-18px_rgba(40,36,120,0.45)]" data-id="element-2726" data-mp="1aq77fmnyopqs">
      <Icon className="w-[17px] h-[17px] text-[#4F46E5] shrink-0" strokeWidth={1.7} aria-hidden="true" data-id="element-2727" data-mp="10tc7czc1ihae" />
      <span className="font-sans text-[12.5px] text-[#121212] whitespace-nowrap" data-id="element-2728" data-mp="3gkqcr2qd1ule">{pick(item.label)}</span>
    </div>;
}
export function TargetedCard({
  isFR
}: P) {
  const pick = pk(isFR);
  return <div className="rounded-2xl border border-[#E6E4F8] bg-white px-4 py-3 flex items-center gap-3 shadow-[0_16px_34px_-20px_rgba(79,70,229,0.55)]" data-id="element-2729" data-mp="5l1wvbtqvpwf">
      <span className="w-10 h-10 rounded-full bg-[#F1F0FD] flex items-center justify-center shrink-0" aria-hidden="true" data-id="element-2730" data-mp="t0pa37iuncq">
        <UsersIcon className="w-5 h-5 text-[#4F46E5]" strokeWidth={1.7} data-id="element-2731" data-mp="3ryjx2v6eq2gy" />
      </span>
      <span data-id="element-2732" data-mp="3n3mplsos0166">
        <span className="block font-sans text-[20px] font-semibold text-[#121212] leading-none tabular-nums" data-id="element-2733" data-mp="3mzyuwq7lh8pv">{pick(targeted.value)}</span>
        <span className="block font-sans text-[11.5px] text-[#5B6478] mt-1 whitespace-nowrap" data-id="element-2734" data-mp="jktfxqz4bwha">{pick(targeted.label)}</span>
      </span>
    </div>;
}
export function Callout({
  isFR
}: P) {
  const lines = isFR ? callout.fr : callout.en;
  return <div className="rounded-2xl border border-[#EEEEF4] bg-white px-4 py-3 flex items-center gap-3 shadow-[0_12px_28px_-22px_rgba(40,36,120,0.45)]" data-id="element-2735" data-mp="9ozutj7c2unw">
      <UserRoundIcon className="w-5 h-5 text-[#4F46E5] shrink-0" strokeWidth={1.7} aria-hidden="true" data-id="element-2736" data-mp="2ucuy38ino0p6" />
      <p className="font-sans text-[12.5px] text-[#121212] leading-snug" data-id="element-2737" data-mp="19vv9cp4pu61x">
        {lines.map(l => <span key={l} className="block" data-id="element-2738" data-mp="1ev85y1le2rhp">
            {l}
          </span>)}
      </p>
    </div>;
}
function Kpi({
  icon: Icon,
  iconTone,
  value,
  label,
  chip,
  chipTone
}: {
  icon: typeof UsersIcon;
  iconTone: 'indigo' | 'red';
  value: string;
  label: string;
  chip: string;
  chipTone: 'green' | 'gray';
}) {
  return <div className="flex items-start gap-3.5 px-4 py-4 min-w-0" data-id="element-2739" data-mp="1tl5krqxfi4u8">
      <span className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 ${iconTone === 'red' ? 'bg-[#FDEEEE]' : 'bg-[#F1F0FD]'}`} aria-hidden="true" data-id="element-2740" data-mp="1xjd3usfd7i8l">
        <Icon className={`w-5 h-5 ${iconTone === 'red' ? 'text-[#DC3B3B]' : 'text-[#4F46E5]'}`} strokeWidth={1.7} data-id="element-2741" data-mp="27hsukuahcphk" />
      </span>
      <div className="min-w-0" data-id="element-2742" data-mp="87kqkiucyv0l">
        <p className="font-sans text-[24px] font-semibold text-[#121212] leading-none tabular-nums" data-id="element-2743" data-mp="d4tc8tepth6l">{value}</p>
        <p className="font-sans text-[12.5px] text-[#5B6478] mt-1" data-id="element-2744" data-mp="53vgqwdx9s1m">{label}</p>
        <span className={`mt-2 inline-flex items-center gap-1 rounded px-2 py-0.5 font-sans text-[11px] whitespace-nowrap ${chipTone === 'green' ? 'bg-[#EEF8F1] text-[#1E8A45]' : 'bg-[#F2F3F6] text-[#6B7180]'}`} data-id="element-2745" data-mp="55ybtutxmbd7">
          {chipTone === 'green' && <ArrowUpIcon className="w-3 h-3" aria-hidden="true" data-id="element-2746" data-mp="a5azghsmnmgu" />}
          {chip}
        </span>
      </div>
    </div>;
}
export function ResultsStrip({
  isFR
}: P) {
  const pick = pk(isFR);
  const r = resultsStrip;
  return <div className="rounded-2xl border border-[#EEEEF4] bg-white shadow-[0_18px_40px_-30px_rgba(40,36,120,0.4)] grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-[1fr_1fr_1fr_1.35fr] divide-y sm:divide-y-0 sm:divide-x divide-[#EFEFF4] p-2" data-id="element-2747" data-mp="xsssshrjislx">
      <Kpi icon={UsersIcon} iconTone="indigo" value={pick(r.reached.value)} label={pick(r.reached.label)} chip={pick(r.reached.chip)} chipTone="green" data-id="element-2748" data-mp="17s1ll7ao616p" />
      <Kpi icon={MailIcon} iconTone="indigo" value={pick(r.rate.value)} label={pick(r.rate.label)} chip={pick(r.rate.chip)} chipTone="green" data-id="element-2749" data-mp="1mdp82z4chhsq" />
      <Kpi icon={AlertTriangleIcon} iconTone="red" value={pick(r.missed.value)} label={pick(r.missed.label)} chip={pick(r.missed.chip)} chipTone="gray" data-id="element-2750" data-mp="1qe9604wqspfx" />
      <div className="sm:col-span-3 lg:col-span-1 !border-l-0 lg:!border-l-0 p-1.5" data-id="element-2751" data-mp="1chvpay018s2i">
        <div className="h-full rounded-xl bg-[#F4F3FD] px-4 py-3.5 flex items-center gap-3" data-id="element-2752" data-mp="1hgp0ukvre901">
          <div className="flex-1 min-w-0" data-id="element-2753" data-mp="12nliibacfr89">
            <p className="flex items-center gap-2 font-sans text-[12.5px] font-medium text-[#4F46E5]" data-id="element-2754" data-mp="17gy8aoqre42l">
              <span className="rounded-md bg-[#4F46E5] px-1.5 py-0.5 text-[9px] font-bold text-white" data-id="element-2755" data-mp="tpt191l2a9i8">{isFR ? 'IA' : 'AI'}</span>
              {pick(r.atna.title)}
            </p>
            <p className="font-sans text-[13px] text-[#121212] leading-snug mt-1.5" data-id="element-2756" data-mp="jr3tw2qsk7g8">{pick(r.atna.body)}</p>
          </div>
          <button type="button" aria-label={pick(r.atna.action)} className="w-11 h-11 rounded-full bg-white shadow-[0_6px_14px_-8px_rgba(79,70,229,0.6)] flex items-center justify-center shrink-0 transition-colors duration-150 hover:bg-[#EEEDFD]" data-id="element-2757" data-mp="oq6pyxosn3c2">
            <ArrowRightIcon className="w-5 h-5 text-[#4F46E5]" strokeWidth={2} aria-hidden="true" data-id="element-2758" data-mp="v6gw1q856yfo" />
          </button>
        </div>
      </div>
    </div>;
}